import { useContext } from 'react';

import type { AxiosError } from 'axios';
import { AppContext } from 'context/App';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  FormError,
  ResetPasswordForm,
  ResetPasswordFormSubmitHandler,
} from 'components/Forms/ResetPasswordForm';

import { useInitServiceCountryFromQuery } from '../../utils';
import { LinkExpired } from './LinkExpired';

export function ResetPassword({ isNewUser = false }: { isNewUser?: boolean }) {
  const context = useContext(AppContext);
  useInitServiceCountryFromQuery(context);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t: translate } = useTranslation('resetPassword');

  const expiry = searchParams.get('expiry');
  const email = searchParams.get('email');

  if (email && expiry && Number(expiry) < Date.now()) {
    return <LinkExpired email={email} isNewUser={isNewUser} />;
  }

  const onSubmit: ResetPasswordFormSubmitHandler = async (data) => {
    const { password } = data;
    const confirmationCode = searchParams.get('confirmationCode');

    if (!email || !confirmationCode) {
      throw new FormError('root', translate('linkExpired'));
    }

    try {
      if (context && context.authentication.verifyForgottenPassword) {
        await context.authentication.verifyForgottenPassword({
          email,
          confirmationCode,
          password,
        });

        navigate('/login/forgot-password/success', {
          state: { newUser: isNewUser },
        });
      } else {
        throw Error(translate('error'));
      }
    } catch (e) {
      const axiosError = e as AxiosError<{ message: string }>;
      const status = axiosError.response?.status;
      const message = axiosError.response?.data.message || '';

      if (status === 400) {
        const isPreviousPasswordError = message.includes(
          'password to a previously used value',
        );
        const errorKey = isPreviousPasswordError
          ? 'newPasswordMustBeDifferent'
          : 'linkExpired';
        throw new FormError('root', translate(errorKey));
      } else if (status === 429) {
        throw new FormError('root', translate('tooManyRequests'));
      } else {
        throw new FormError('root', translate('error'));
      }
    }
  };

  return (
    <ResetPasswordForm
      onSubmit={onSubmit}
      submitText={translate(isNewUser ? 'continue' : 'updatePassword')}
      isNewUser={isNewUser}
    />
  );
}
